import { useQuery, queryOptions } from "@tanstack/react-query";
import { apiClient, signApiRequest } from "./";
import { AxiosError } from "axios";
import { CustomerCredentials } from "../ui/customerDataStore";
import { captureException } from "@sentry/react";
import { setStatsigUserId } from "../../utils/statsig";

export const getCustomerInfoById = async ({
  credentials,
}: {
  credentials: CustomerCredentials;
}) => {
  const signedRequest = await signApiRequest(
    "GET",
    `${import.meta.env.VITE_API_URL}/user/user?fid=${credentials.identityId}`,
    credentials,
    {
      "content-type": "application/json",
    },
  );

  const response = await apiClient(signedRequest);

  // Covers the post-POA case: sign-in returns no uuid until user-service has synced it.
  void setStatsigUserId(response.data?.[0]?.user?.customerUuid);

  return response.data;
};

export const getCustomerQueryOptions = (credentials: CustomerCredentials) =>
  queryOptions({
    queryKey: ["get-customer-info-by-id"],
    queryFn: () => getCustomerInfoById({ credentials }),
    retry: false,
    staleTime: 30_000,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-customer-info-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: "/user/user?fid=${identityId}",
        },
        level: "error",
      });
      return true;
    },
  });

export const useGetCustomerInfoById = ({
  credentials,
  enabled,
}: {
  credentials: CustomerCredentials;
  enabled?: boolean;
}) => {
  const customerInfoQuery = useQuery<TCustomerInfoResponse, AxiosError>({
    queryKey: ["get-customer-info-by-id"],
    queryFn: () => getCustomerInfoById({ credentials }),
    enabled,
    retry: false,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-customer-info-by-id",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: "/user/user?fid=${identityId}",
        },
        level: "error",
      });
      return true;
    },
  });
  return customerInfoQuery;
};

interface TUser {
  secrecyMarked?: boolean;
  sex: string;
  deviceToken?: string;
  isFreemium?: boolean;
  birthDate: string;
  isInPlanningPhase: boolean;
  insurancesForTransfer?: string;
  email: string;
  ssn: string;
  isWelcomeEmailSent?: boolean;
  knowledgeAssessmentDone?: boolean;
  lastName: string;
  channelType?: string;
  fid: string;
  firstName: string;
  name: string;
  notifyInsuranceUpdate: boolean;
  createdAt: string;
  updatedAt?: string;
  salary: string | number;
  phoneNumber?: string;
  phone: string;
  citizenship?: string;
  isMarried?: boolean;
  age?: number;
  latestMISFileAt?: string;
  agentId?: string | null;
  activeCaseId?: string;
  customerUuid?: string;
}

interface TAddress {
  address1: string;
  city: string;
  postCode: string;
}

interface TCountry {
  alpha2: string;
  alpha3?: string;
}

interface TKYC {
  riskTermAssociation: string;
  savingsGapStrategy: string;
  riskTolerance: string;
  previousFundExperience: string;
  sustainability: string;
  savingPurpose: string;
  returnCheckFrequency: string;
  isTaxLiableInUSA: boolean;
  isPEP: boolean;
  isRelatedToPEP: boolean;
  isCoWorkerToPEP?: boolean;
  isCoworkerToPEP?: boolean;
  isResidentInSweden: boolean;
  citizenship: string;
  hasOtherTaxResidency?: boolean;
}

type TCustomerInfo = {
  user: TUser;
  address: TAddress;
  country: TCountry;
  activeCaseId?: string;
  kyc: TKYC;
  booking: {
    bookingId: string | null;
    startsAt: string | null;
    status?: string;
  };
  poa: {
    poaId: string | null;
    validTo: string | null;
    isValid?: boolean;
  };
  appData?: {
    engagement?: { ctaClickedAt?: string; insurelyInitiatedAt?: string };
    contactTopics?: string[];
  } | null;
};

export type TCustomerInfoResponse = TCustomerInfo[];
